/**
 * Fallback when package page "Back" has no history and no return path in location state.
 */
export const LOGGED_USER_DASHBOARD_PATH = "/dashboard";

export const STORE_HANDBOOKS_FALLBACK = {
  pathname: "/store",
  state: { tab: "hand_books" },
};

export const PACKAGE_RETURN_TO_STATE_KEY = "returnTo";
export const PACKAGE_RETURN_TO_SESSION_KEY = "motion.packageReturnTo";

export const PRACTICE_FALLBACK_PATH = "/practice";
export const ADAPTIVE_QBANK_LISTING_PATH = "/practice/adaptive-question-bank";
export const ADAPTIVE_QBANK_NEXT_FOCUS_PATH =
  "/practice/adaptive-question-bank/next-focus-set";
export const ADAPTIVE_QBANK_TEST_PATH =
  "/practice/adaptive-question-bank/next-focus-set/test";
export const EXPLORE_COURSES_PATH = "/dashboard/courses";
export const STORE_PATH = "/store";
export const YEARWISE_LISTING_PATH = "/practice/paper-listing?type=Year Wise";
export const PAPER_LISTING_PATH = "/practice/paper-listing";
export const LISTING_TAB_QUERY = "tab";

const listingQueryFrom = (value) => {
  if (value == null) return "";
  const raw = String(value);
  const queryIndex = raw.indexOf("?");
  if (queryIndex >= 0) return raw.slice(queryIndex + 1);
  return raw.replace(/^\?/, "");
};

export function readListingTab(searchOrPath) {
  const tab = new URLSearchParams(listingQueryFrom(searchOrPath)).get(
    LISTING_TAB_QUERY
  );
  return tab && tab.trim() ? tab : null;
}

export function buildListingSearchWithTab(search, tab, defaultTab) {
  const raw = listingQueryFrom(search);
  const params = new URLSearchParams(raw);
  const currentTab = params.get(LISTING_TAB_QUERY);
  const nextTab = !tab || tab === defaultTab ? null : tab;
  if ((currentTab || null) === nextTab) {
    return raw ? `?${raw}` : "";
  }

  const parts = raw
    .split("&")
    .filter(Boolean)
    .filter((part) => {
      const key = decodeURIComponent(part.split("=")[0].replace(/\+/g, " "));
      return key !== LISTING_TAB_QUERY;
    });

  if (nextTab) {
    parts.push(`${LISTING_TAB_QUERY}=${encodeURIComponent(nextTab)}`);
  }

  return parts.length ? `?${parts.join("&")}` : "";
}

export function withListingTab(path, ...candidates) {
  if (!path || !String(path).includes("/practice/paper-listing")) return path;

  const raw = String(path);
  const queryIndex = raw.indexOf("?");
  const pathname = queryIndex >= 0 ? raw.slice(0, queryIndex) : raw;
  const params = new URLSearchParams(
    queryIndex >= 0 ? raw.slice(queryIndex + 1) : ""
  );

  if (!params.get(LISTING_TAB_QUERY)) {
    for (const candidate of candidates) {
      const tab = readListingTab(candidate);
      if (tab) {
        params.set(LISTING_TAB_QUERY, tab);
        break;
      }
    }
  }

  const nextSearch = params.toString();
  return nextSearch ? `${pathname}?${nextSearch}` : pathname;
}
export const PYQ_YEAR_SESSION_KEY = "motion.pyqYearWiseSelectedYear";

export function persistSelectedPyqYear(year) {
  if (year == null || String(year).trim() === "") return;
  try {
    sessionStorage.setItem(PYQ_YEAR_SESSION_KEY, String(year));
  } catch (_) {
    // Ignore quota / private-mode failures.
  }
}

export function readPersistedPyqYear() {
  try {
    const stored = sessionStorage.getItem(PYQ_YEAR_SESSION_KEY);
    return stored && stored.trim() !== "" ? stored : null;
  } catch (_) {
    return null;
  }
}

export function getYearFromListingLocation(location) {
  const search =
    typeof location === "string"
      ? location.includes("?")
        ? location.slice(location.indexOf("?"))
        : ""
      : location?.search || "";

  const fromQuery = new URLSearchParams(search).get("year");
  if (fromQuery) return fromQuery;

  if (location && typeof location === "object") {
    return location.state?.year ?? null;
  }

  return null;
}

export function buildYearWiseListingPath(year) {
  const yearQuery =
    year != null && String(year).trim() !== ""
      ? `&year=${encodeURIComponent(String(year))}`
      : "";
  return `/practice/paper-listing?type=Year Wise${yearQuery}`;
}

export function ensureYearOnYearWisePath(path, year) {
  if (!path) return buildYearWiseListingPath(year);
  const raw = String(path);
  const queryIndex = raw.indexOf("?");
  const pathname = queryIndex >= 0 ? raw.slice(0, queryIndex) : raw;
  const search = queryIndex >= 0 ? raw.slice(queryIndex + 1) : "";

  if (!pathname.includes("/practice/paper-listing")) {
    return raw;
  }

  const params = new URLSearchParams(search);
  if (!params.get("type")) {
    params.set("type", "Year Wise");
  }
  if (year != null && String(year).trim() !== "" && !params.get("year")) {
    params.set("year", String(year));
  }

  const nextSearch = params.toString();
  return nextSearch ? `${pathname}?${nextSearch}` : pathname;
}

export function resolveYearWiseReturnPath({ location, testDetails } = {}) {
  const year =
    testDetails?.year ??
    getYearFromListingLocation(location) ??
    location?.state?.year ??
    readPersistedPyqYear();
  return buildYearWiseListingPath(year);
}
export const TOPICWISE_SETS_PATH = "/practice/topic-wise/pyqset";
export const MISTAKE_BOOK_SETS_PATH = "/practice/mistake-book/practice-sets";

export function getLocationListingPath(location) {
  return getLocationPath(location) || null;
}

export function getSourceListingPath(source) {
  switch (source) {
    case "yearwise":
      return YEARWISE_LISTING_PATH;
    case "topicwise":
      return TOPICWISE_SETS_PATH;
    case "mistake_book_practice_set":
      return MISTAKE_BOOK_SETS_PATH;
    case "dashboard":
      return "/dashboard";
    case "paperlisting":
      return PAPER_LISTING_PATH;
    default:
      return null;
  }
}

const isCompatibleReturnPath = (path, source) => {
  const normalized = normalizePathForCompare(path);
  if (!normalized || normalized === "/") return false;

  const isMistakeBook = normalized.includes("/practice/mistake-book");
  if (isMistakeBook) {
    return source === "mistake_book_practice_set";
  }

  if (source === "yearwise") {
    return (
      normalized.includes("/practice/paper-listing") ||
      normalized === "/practice" ||
      normalized === "/dashboard"
    );
  }
  if (source === "paperlisting") {
    return (
      normalized.includes("/practice/paper-listing") ||
      normalized === "/practice" ||
      normalized === "/dashboard"
    );
  }
  if (source === "topicwise") {
    return (
      normalized.includes("/practice/topic-wise") ||
      normalized === "/practice" ||
      normalized === "/dashboard"
    );
  }
  if (source === "dashboard") {
    return normalized === "/dashboard" || normalized.startsWith("/dashboard/");
  }
  return !isMistakeBook;
};

/** Immediate parent for analytics / summary Back — never a leftover section. */
export function resolveImmediateReturnPath({
  location,
  source,
  testDetails,
} = {}) {
  const selectedYear =
    testDetails?.year ??
    getYearFromListingLocation(location) ??
    location?.state?.year ??
    readPersistedPyqYear();

  const withYear = (path) => {
    if (!path) return path;
    const isYearWiseReturn =
      source === "yearwise" ||
      String(path).includes("type=Year Wise") ||
      String(path).includes("type=Year%20Wise");
    return isYearWiseReturn ? ensureYearOnYearWisePath(path, selectedYear) : path;
  };

  const fromRoute =
    location?.state?.[PACKAGE_RETURN_TO_STATE_KEY] ?? location?.state?.returnTo;
  const fromDetails = testDetails?.returnTo;
  const tabSources = [fromRoute, fromDetails, location?.search];
  const keepListingTab = (path) => withListingTab(path, ...tabSources);

  if (fromRoute && isCompatibleReturnPath(fromRoute, source)) {
    return keepListingTab(withYear(fromRoute));
  }

  if (source === "yearwise") {
    return keepListingTab(buildYearWiseListingPath(selectedYear));
  }

  const listing = getSourceListingPath(source);
  if (listing) return keepListingTab(withYear(listing));

  if (fromDetails && isCompatibleReturnPath(fromDetails, source)) {
    return keepListingTab(withYear(fromDetails));
  }

  return PRACTICE_FALLBACK_PATH;
}

const isNonEmptyString = (value) => typeof value === "string" && value.trim().length > 0;

const getLocationPath = (location) =>
  `${location?.pathname || ""}${location?.search || ""}`.trim();

const normalizePathForCompare = (path) => {
  const raw = String(path || "").trim().split("#")[0].split("?")[0];
  if (!raw) return "/";
  const withLeadingSlash = raw.startsWith("/") ? raw : `/${raw}`;
  return withLeadingSlash.replace(/\/+$/, "").toLowerCase() || "/";
};

/** True when `candidate` is a nested child of `current` (e.g. listing vs next-focus-set). */
const isDescendantPath = (candidate, current) => {
  const child = normalizePathForCompare(candidate);
  const parent = normalizePathForCompare(current);
  if (!child || !parent || parent === "/") return false;
  return child !== parent && child.startsWith(`${parent}/`);
};

const isPackageDetailPath = (path) =>
  /^\/package\/[^/]+$/i.test(normalizePathForCompare(path));

const normalizeReturnToInput = (returnTo) => {
  if (!isNonEmptyString(returnTo)) return null;

  const trimmed = returnTo.trim();

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const parsed = new URL(trimmed);
      if (parsed.origin !== window.location.origin) return null;
      return `${parsed.pathname}${parsed.search}`.trim() || null;
    }
  } catch {
    return null;
  }

  return trimmed;
};

const sanitizeReturnTo = (returnTo, location, options = {}) => {
  const { allowSameAsCurrent = false } = options;
  const normalized = normalizeReturnToInput(returnTo);
  if (!normalized) return null;

  if (isPackageDetailPath(normalized)) return null;

  if (location) {
    const currentPath = getLocationPath(location);
    const currentNorm = normalizePathForCompare(currentPath);
    const returnNorm = normalizePathForCompare(normalized);

    if (!allowSameAsCurrent && returnNorm === currentNorm) {
      return null;
    }

    // Never treat a child of the current page as "where I came from".
    // This stops Adaptive QBank listing ↔ next-focus-set back-arrow loops.
    if (isDescendantPath(normalized, currentPath)) {
      return null;
    }
  }

  return normalized;
};

const sanitizeOutboundReturnTo = (returnTo, location) =>
  sanitizeReturnTo(returnTo, location, { allowSameAsCurrent: true });

/**
 * @param {{ pathname?: string, search?: string, state?: object } | null | undefined} location
 */
export function getCurrentReturnPath(location) {
  if (!location) return null;

  const existing =
    location.state?.[PACKAGE_RETURN_TO_STATE_KEY] ?? location.state?.returnTo;

  const sanitizedExisting = sanitizeReturnTo(existing, location);
  if (sanitizedExisting) return sanitizedExisting;

  const currentPath = getLocationPath(location);
  if (isPackageDetailPath(currentPath)) return null;

  return currentPath || null;
}

export function isExploreMotionReturnPath(path) {
  const normalized = normalizePathForCompare(path);
  const coursesRoot = normalizePathForCompare(EXPLORE_COURSES_PATH);
  const dashboardRoot = normalizePathForCompare("/dashboard");
  return (
    normalized === coursesRoot ||
    normalized.startsWith(`${coursesRoot}/`) ||
    normalized === dashboardRoot
  );
}

export function shouldUseExploreMotionHeader(location) {
  const returnTo = resolveReturnTo(location);
  return Boolean(returnTo && isExploreMotionReturnPath(returnTo));
}

/** Hide the fixed app header on package / handbook checkout when opened from dashboard. */
export function shouldHideStoreCheckoutHeader(location) {
  return shouldUseExploreMotionHeader(location);
}

export function setPackageReturnTo(returnTo) {
  persistReturnTo(returnTo);
}

const persistReturnTo = (returnTo) => {
  const sanitized = sanitizeReturnTo(returnTo);
  if (!sanitized) return;
  try {
    sessionStorage.setItem(PACKAGE_RETURN_TO_SESSION_KEY, sanitized);
  } catch {
    // ignore storage failures
  }
};

const readPersistedReturnTo = () => {
  try {
    return sessionStorage.getItem(PACKAGE_RETURN_TO_SESSION_KEY);
  } catch {
    return null;
  }
};

const clearPersistedReturnTo = () => {
  try {
    sessionStorage.removeItem(PACKAGE_RETURN_TO_SESSION_KEY);
  } catch {
    // ignore storage failures
  }
};

const getPracticeLeaf = (path) => {
  const normalized = normalizePathForCompare(path);
  if (normalized.includes("/practice/mistake-book")) return "mistake-book";
  if (normalized.includes("/practice/paper-listing")) return "paper-listing";
  if (normalized.includes("/practice/topic-wise")) return "topic-wise";
  if (normalized.includes("/practice/adaptive-question-bank")) return "adaptive";
  if (normalized.includes("/question_bank")) return "question-bank";
  return null;
};

const resolveReturnTo = (location) => {
  const fromState =
    location?.state?.[PACKAGE_RETURN_TO_STATE_KEY] ?? location?.state?.returnTo;
  const sanitizedState = sanitizeReturnTo(fromState, location);
  if (sanitizedState) return sanitizedState;

  const fromSession = sanitizeReturnTo(readPersistedReturnTo(), location);
  if (fromSession) {
    const sessionLeaf = getPracticeLeaf(fromSession);
    const currentLeaf = getPracticeLeaf(getLocationPath(location));
    if (sessionLeaf && currentLeaf && sessionLeaf !== currentLeaf) {
      return null;
    }
    return fromSession;
  }

  return null;
};

/**
 * Open a package detail page and remember where the user came from for the Back button.
 *
 * @param {{ push: (to: string | object) => void, location?: object }} history
 * @param {string | number} packageId
 * @param {{ returnTo?: string, extraState?: object }} [options]
 */
export function navigateToPackagePage(history, packageId, options = {}) {
  if (!history || packageId == null || packageId === "") return;

  const { returnTo, extraState } = options;
  const resolvedReturnTo = sanitizeOutboundReturnTo(
    returnTo ?? getCurrentReturnPath(history.location),
    history.location
  );

  if (resolvedReturnTo) {
    persistReturnTo(resolvedReturnTo);
  }

  history.push({
    pathname: `/package/${packageId}`,
    state: {
      ...(extraState || {}),
      ...(resolvedReturnTo ? { [PACKAGE_RETURN_TO_STATE_KEY]: resolvedReturnTo } : {}),
    },
  });
}

/**
 * Open a handbook detail page and remember where the user came from for the Back button.
 *
 * @param {{ push: (to: string | object) => void, location?: object }} history
 * @param {string | number} handbookId
 * @param {{ returnTo?: string, extraState?: object }} [options]
 */
export function navigateToHandbookPage(history, handbookId, options = {}) {
  if (!history || handbookId == null || handbookId === "") return;

  const { returnTo, extraState } = options;
  const resolvedReturnTo = sanitizeOutboundReturnTo(
    returnTo ?? getCurrentReturnPath(history.location),
    history.location
  );

  if (resolvedReturnTo) {
    persistReturnTo(resolvedReturnTo);
  }

  history.push({
    pathname: `/store/handbook/${handbookId}`,
    state: {
      ...(extraState || {}),
      ...(resolvedReturnTo ? { [PACKAGE_RETURN_TO_STATE_KEY]: resolvedReturnTo } : {}),
    },
  });
}

function goBackWithReturnTo(history, location, fallback) {
  if (!history) return;

  const returnTo = resolveReturnTo(location);
  clearPersistedReturnTo();

  if (returnTo && typeof history.goBack === "function" && (history.length ?? 0) > 1) {
    history.goBack();
    return;
  }

  if (returnTo && typeof history.replace === "function") {
    history.replace(returnTo);
    return;
  }

  if (typeof history.goBack === "function" && (history.length ?? 0) > 1) {
    history.goBack();
    return;
  }

  if (typeof history.replace === "function") {
    if (typeof fallback === "object" && fallback !== null) {
      history.replace(fallback);
    } else {
      history.replace(fallback);
    }
  }
}

/**
 * Package detail `StoreBanner` Back — return to the page the user came from.
 */
export function goBackFromPackagePage(history, location) {
  if (!history?.replace) return;

  const returnTo = resolveReturnTo(location) || STORE_PATH;

  clearPersistedReturnTo();
  history.replace(returnTo);
}

/**
 * Handbook detail `CartHeader` Back — return to the page the user came from.
 */
export function goBackFromHandbookPage(history, location) {
  const returnTo = resolveReturnTo(location);
  const fallback =
    returnTo && isExploreMotionReturnPath(returnTo)
      ? returnTo
      : STORE_HANDBOOKS_FALLBACK;
  goBackWithReturnTo(history, location, fallback);
}

/**
 * Navigate from Practice / Quick Actions and remember where the user came from.
 *
 * @param {{ push: (to: string | object, state?: object) => void, location?: object }} history
 * @param {string | { pathname: string, search?: string, state?: object }} to
 * @param {object} [extraState]
 */
export function navigateFromPracticeOrigin(history, to, extraState) {
  if (!history || typeof history.push !== "function") return;

  const resolvedReturnTo = sanitizeOutboundReturnTo(
    extraState?.[PACKAGE_RETURN_TO_STATE_KEY] ??
      extraState?.returnTo ??
      getCurrentReturnPath(history.location),
    history.location
  );

  if (resolvedReturnTo) {
    persistReturnTo(resolvedReturnTo);
  }

  const mergeState = (state = {}) => ({
    ...state,
    ...(extraState || {}),
    ...(resolvedReturnTo ? { [PACKAGE_RETURN_TO_STATE_KEY]: resolvedReturnTo } : {}),
  });

  if (typeof to === "string") {
    const queryIndex = to.indexOf("?");
    const pathname = queryIndex >= 0 ? to.slice(0, queryIndex) : to;
    const search = queryIndex >= 0 ? to.slice(queryIndex) : "";
    const merged = mergeState(extraState);

    if (search) {
      history.push({ pathname, search, state: merged });
      return;
    }

    if (merged && Object.keys(merged).length > 0) {
      history.push(pathname, merged);
      return;
    }

    history.push(to);
    return;
  }

  history.push({
    ...to,
    state: mergeState(to?.state),
  });
}

const replaceOrPush = (history, to) => {
  if (!history) return;
  if (typeof history.replace === "function") {
    history.replace(to);
    return;
  }
  if (typeof history.push === "function") {
    history.push(to);
  }
};

const pickAdaptiveReturnTo = (location, parentPath) => {
  const fromState =
    location?.state?.[PACKAGE_RETURN_TO_STATE_KEY] ?? location?.state?.returnTo;
  return sanitizeReturnTo(fromState, { pathname: parentPath });
};

/**
 * Adaptive QBank topic page Back — always the topic listing, never a push
 * that would re-enter next-focus-set via polluted returnTo.
 */
export function goBackToAdaptiveQuestionBankListing(history, location, extraState = {}) {
  const safeReturnTo = pickAdaptiveReturnTo(location, ADAPTIVE_QBANK_LISTING_PATH);
  replaceOrPush(history, {
    pathname: ADAPTIVE_QBANK_LISTING_PATH,
    state: {
      ...extraState,
      ...(safeReturnTo ? { [PACKAGE_RETURN_TO_STATE_KEY]: safeReturnTo } : {}),
    },
  });
}

/**
 * Adaptive QBank nested screens Back — always the focus-set page, via replace.
 */
export function goBackToAdaptiveNextFocusSet(history, location, extraState = {}) {
  const prev = location?.state || {};
  const definedExtraState = Object.fromEntries(
    Object.entries(extraState).filter(([, value]) => value !== undefined)
  );
  const safeReturnTo = pickAdaptiveReturnTo(location, ADAPTIVE_QBANK_NEXT_FOCUS_PATH);
  replaceOrPush(history, {
    pathname: ADAPTIVE_QBANK_NEXT_FOCUS_PATH,
    state: {
      topic: definedExtraState.topic ?? prev.topic ?? null,
      topic_id: definedExtraState.topic_id ?? prev.topic_id ?? prev.topic?.topic_id,
      topic_name:
        definedExtraState.topic_name ?? prev.topic_name ?? prev.topic?.topic_name,
      selectedSubject:
        definedExtraState.selectedSubject ??
        prev.selectedSubject ??
        prev.subject ??
        prev.topic?.subject ??
        prev.topic?.subject_name ??
        prev.topic?.sub_name,
      ...(safeReturnTo ? { [PACKAGE_RETURN_TO_STATE_KEY]: safeReturnTo } : {}),
      ...definedExtraState,
    },
  });
}

/**
 * Practice sub-pages Back — return to the page the user came from (e.g. /dashboard).
 * Uses replace (never goBack) so summary/analytics ↔ paper-listing cannot loop.
 */
export function goBackFromPracticePage(history, location, fallback = PRACTICE_FALLBACK_PATH) {
  if (!history) return;

  const returnTo = resolveReturnTo(location);
  clearPersistedReturnTo();

  const examFlowPrefixes = [
    "/practice-summary",
    "/analytics",
    "/adv-analytics",
    "/test",
    ADAPTIVE_QBANK_TEST_PATH,
    "/testinstruction",
    "/practice-view-solution",
  ];

  const isExamFlowPath = (path) => {
    const normalized = normalizePathForCompare(path);
    return examFlowPrefixes.some(
      (prefix) =>
        normalized === normalizePathForCompare(prefix) ||
        normalized.startsWith(`${normalizePathForCompare(prefix)}/`)
    );
  };

  const currentPath = getLocationPath(location);
  const target =
    returnTo &&
    !isExamFlowPath(returnTo) &&
    !isDescendantPath(returnTo, currentPath)
      ? returnTo
      : fallback;

  if (typeof history.replace === "function") {
    history.replace(target);
    return;
  }

  if (typeof history.push === "function") {
    history.push(target);
  }
}

/** @deprecated Use goBackFromPackagePage(history, location) */
export function goToLoggedUserDashboardFromPackagePage(history, location) {
  goBackFromPackagePage(history, location);
}

const extractPackageIdFromPath = (path) => {
  const raw = String(path || "").trim();
  if (!raw) return null;

  let pathname = raw;
  let search = "";

  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      pathname = parsed.pathname;
      search = parsed.search;
    } else {
      const queryIndex = raw.indexOf("?");
      pathname = queryIndex >= 0 ? raw.slice(0, queryIndex) : raw;
      search = queryIndex >= 0 ? raw.slice(queryIndex) : "";
    }
  } catch {
    pathname = raw.split("?")[0].split("#")[0];
  }

  pathname = (pathname.startsWith("/") ? pathname : `/${pathname}`)
    .split("#")[0];

  const match = pathname.match(/^\/package\/([^/?#]+)\/?$/i);
  if (match) return match[1];

  if (/^\/package\/?$/i.test(pathname) && search) {
    try {
      const params = new URLSearchParams(search);
      const id = params.get("id") ?? params.get("package_id");
      if (id) return id;
    } catch {
      // ignore parse failures
    }
  }

  return null;
};

const normalizeBannerRedirectPath = (redirectRaw) => {
  const trimmed = String(redirectRaw || "").trim();
  if (!trimmed) return "";
  return `/${trimmed.replace(/^\/+/, "")}`.toLowerCase();
};

/**
 * Navigate from the logged-in **user dashboard** (banners, sliders, promos,
 * combo blocks, etc.). Prefer this over ad-hoc `history.push` for in-app routes
 * opened from the dashboard so behaviour stays aligned with package-page back.
 *
 * @param {{ push: (to: string | object, state?: object) => void, location?: object }} history
 * @param {string | { pathname: string, search?: string, state?: object }} to
 * @param {object} [locationState] - only when `to` is a string path for `/store` with `history.push(path, state)` style
 */
export function navigateFromUserDashboardBanner(history, to, locationState) {
  if (!history || typeof history.push !== "function") return;

  const resolvedReturnTo = sanitizeReturnTo(
    locationState?.[PACKAGE_RETURN_TO_STATE_KEY] ??
      locationState?.returnTo ??
      getCurrentReturnPath(history.location),
    history.location
  );

  if (typeof to === "string") {
    const packageId = extractPackageIdFromPath(to);
    if (packageId) {
      navigateToPackagePage(history, packageId, {
        returnTo: resolvedReturnTo,
        extraState: locationState,
      });
      return;
    }

    if (locationState !== undefined) {
      history.push(to, locationState);
    } else {
      history.push(to);
    }
    return;
  }

  const packageId = extractPackageIdFromPath(to?.pathname);
  if (packageId) {
    navigateToPackagePage(history, packageId, {
      returnTo: resolvedReturnTo,
      extraState: to?.state,
    });
    return;
  }

  history.push(to);
}

export function navigateFromDashboardSliderBanner(history, element, forcedReturnTo) {
  if (!history || !element) return;

  const returnTo =
    sanitizeReturnTo(
      forcedReturnTo ?? getCurrentReturnPath(history.location),
      history.location
    ) || EXPLORE_COURSES_PATH;

  const redirectRaw = (element.redirect_path || "").trim();
  const redirectLower = redirectRaw.toLowerCase();

  if (redirectLower === "store") {
    if (element.module_id) {
      navigateFromUserDashboardBanner(history, "/store", {
        targetModuleId: element.module_id,
        [PACKAGE_RETURN_TO_STATE_KEY]: returnTo,
      });
      return;
    }
    navigateFromUserDashboardBanner(history, "/store", {
      [PACKAGE_RETURN_TO_STATE_KEY]: returnTo,
    });
    return;
  }

  if (redirectLower === "web view") {
    const url = element.web_url || element.app_url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    return;
  }

  if (!redirectRaw) return;

  if (
    redirectLower.startsWith("http://") ||
    redirectLower.startsWith("https://")
  ) {
    window.open(redirectRaw, "_blank", "noopener,noreferrer");
    return;
  }

  const path = normalizeBannerRedirectPath(redirectRaw);
  navigateFromUserDashboardBanner(history, path, {
    [PACKAGE_RETURN_TO_STATE_KEY]: returnTo,
  });
}
